import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { testimonials } from '@/mocks/testimonials';

const extendedTestimonials = [
  ...testimonials,
  {
    id: '5',
    quote: "I was skeptical about online AI courses, but the project-based approach here is unmatched. Built a working RAG pipeline during the course and deployed it at work the next week. My manager was impressed.",
    name: 'Marcus Chen',
    role: 'ML Engineer at Grab',
    initials: 'MC',
    initialsBg: 'bg-accent-600',
    course: 'AI for Developers',
    rating: 5,
  },
  {
    id: '6',
    quote: "The instructors explain complex topics like transformer architectures in a way that actually makes sense. No jargon, no showing off — just clear, practical teaching. Best money I have spent on education.",
    name: 'Sarah Lindqvist',
    role: 'PhD Candidate, KTH Royal Institute',
    initials: 'SL',
    initialsBg: 'bg-secondary-600',
    course: 'Machine Learning Foundations',
    rating: 5,
  },
  {
    id: '7',
    quote: "Coming from a non-technical background, I thought AI was out of reach. The ChatGPT course proved me wrong. Within 2 weeks I was building custom GPTs for my marketing team. Game changer.",
    name: 'Amira Yusof',
    role: 'Marketing Director at AirAsia',
    initials: 'AY',
    initialsBg: 'bg-primary-700',
    course: 'ChatGPT Mastery',
    rating: 5,
  },
  {
    id: '8',
    quote: "The AI Agents course opened up an entirely new career path for me. I now lead the automation team at my company, all because I took a chance on this course. Cannot recommend it enough.",
    name: 'David Park',
    role: 'Automation Lead at Razer',
    initials: 'DP',
    initialsBg: 'bg-accent-700',
    course: 'AI Agents & Automation',
    rating: 5,
  },
];

const companies = [
  'Stripe', 'Netflix', 'Notion', 'Grab', 'AirAsia', 'Razer', 'KTH', 'OpenAI',
];

export default function TestimonialsPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-primary-500 uppercase tracking-widest mb-3">
            Student Stories
          </p>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
            Trusted by Learners Worldwide
          </h1>
          <p className="text-base text-foreground-600 max-w-2xl mx-auto">
            Hear from students who have transformed their careers with our AI courses — from complete beginners to seasoned engineers.
          </p>
        </div>
      </section>

      {/* Companies strip */}
      <section className="border-b border-background-200 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-xs text-foreground-500 uppercase tracking-widest text-center mb-5">
            Our students work at
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {companies.map((company) => (
              <span
                key={company}
                className="text-sm font-semibold text-foreground-400 whitespace-nowrap"
              >
                {company}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {extendedTestimonials.map((t) => (
              <div
                key={t.id}
                className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-7 transition-colors hover:border-primary-300"
              >
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i
                      key={i}
                      className={`ri-star-fill text-sm w-4 h-4 flex items-center justify-center ${
                        i < t.rating ? 'text-primary-400' : 'text-background-300'
                      }`}
                    />
                  ))}
                </div>

                <blockquote className="text-sm text-foreground-700 leading-relaxed mb-5">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>

                <div className="flex items-center gap-3 mb-3">
                  <div
                    className={`w-10 h-10 rounded-full ${t.initialsBg} flex items-center justify-center text-background-50 text-sm font-bold shrink-0`}
                  >
                    {t.initials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>

                <span className="inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2.5 py-0.5 font-medium whitespace-nowrap transition-colors bg-primary-100 text-primary-700 text-xs">
                  {t.course}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-16 bg-foreground-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">
            Join 36,000+ happy learners
          </h2>
          <p className="text-sm text-foreground-400 mb-6 max-w-md mx-auto">
            Start your AI journey today. Zero risk with our satisfaction guarantee.
          </p>
          <Link
            to="/courses"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none bg-primary-500 text-background-50 hover:bg-primary-600 h-10 px-5 text-sm"
          >
            Explore Courses
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}